import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Image, Loader2, Trash2, X, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full URL with cache busting
const getFullLogoUrl = (url, bustCache = false) => {
  if (!url) return null;
  let fullUrl = url;
  if (url.startsWith('/uploads')) {
    fullUrl = `${API}/api${url}`;
  } else if (url.startsWith('/')) {
    fullUrl = `${API}${url}`;
  } else if (!url.startsWith('http')) {
    fullUrl = `${API}/${url}`;
  }
  // Add cache busting if requested
  if (bustCache) {
    const separator = fullUrl.includes('?') ? '&' : '?';
    return `${fullUrl}${separator}v=${Date.now()}`;
  }
  return fullUrl;
};

export default function AdminLogoSettings() {
  const navigate = useNavigate();
  const primaryFileInputRef = useRef(null);
  const secondaryFileInputRef = useRef(null);
  
  const [logoSettings, setLogoSettings] = useState({
    logo_url: null,
    logo_light_url: null,
    updated_at: null
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingLight, setUploadingLight] = useState(false);
  const [cacheKey, setCacheKey] = useState(Date.now()); // For cache busting
  const [previewModal, setPreviewModal] = useState({ open: false, url: null, title: '' });

  useEffect(() => {
    fetchLogoSettings();
  }, []);

  const fetchLogoSettings = async () => {
    try {
      const res = await fetch(`${API}/api/admin/logo-settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLogoSettings(data);
        setCacheKey(Date.now()); // Refresh cache key
      }
    } catch (err) {
      console.error('Failed to fetch logo settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, isLightVersion = false) => {
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload PNG, JPEG, SVG, or WebP');
      return;
    }

    if (isLightVersion) {
      setUploadingLight(true);
    } else {
      setUploading(true);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('logo_type', isLightVersion ? 'light' : 'default');

      const res = await fetch(`${API}/api/admin/logo-settings/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Upload failed');
      }

      const data = await res.json();
      
      // Update state with new logo URL
      setLogoSettings(prev => ({
        ...prev,
        [isLightVersion ? 'logo_light_url' : 'logo_url']: data.logo_url,
        updated_at: data.updated_at
      }));
      
      // Force refresh cache key to bust browser cache
      setCacheKey(Date.now());
      
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
      setUploadingLight(false);
    }
  };

  const handleDelete = async (isLightVersion = false) => {
    if (!window.confirm('Are you sure you want to remove this logo?')) return;

    try {
      const res = await fetch(`${API}/api/admin/logo-settings/delete`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logo_type: isLightVersion ? 'light' : 'default' })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Delete failed');
      }

      setLogoSettings(prev => ({
        ...prev,
        [isLightVersion ? 'logo_light_url' : 'logo_url']: null
      }));
      
      setCacheKey(Date.now());
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openPreviewModal = (url, title) => {
    setPreviewModal({ open: true, url: getFullLogoUrl(url, true), title });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF9E00]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFD] to-[#E8EEF3]">
      {/* Full Size Preview Modal */}
      {previewModal.open && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8"
          onClick={() => setPreviewModal({ open: false, url: null, title: '' })}
        >
          <div className="relative max-w-4xl w-full bg-white rounded-2xl p-6" onClick={e => e.stopPropagation()}>
            <button
              onClick={() => setPreviewModal({ open: false, url: null, title: '' })}
              className="absolute top-4 right-4 w-10 h-10 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
            <h3 className="text-lg font-semibold text-[#1E3A5C] mb-4">{previewModal.title}</h3>
            <div className="bg-slate-100 rounded-xl p-8 flex items-center justify-center min-h-[300px]">
              <img 
                src={previewModal.url} 
                alt={previewModal.title}
                className="max-w-full max-h-[400px] object-contain"
              />
            </div>
            <p className="text-xs text-slate-500 mt-4 text-center">
              URL: {previewModal.url?.split('?')[0]}
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-[#E8EEF3]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="ghost"
            className="text-[#4A5E75] hover:text-[#1E3A5C] hover:bg-[#E8EEF3] mb-4"
            data-testid="back-to-admin-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-[#FF9E00]/10 rounded-xl">
              <Image className="w-6 h-6 text-[#FF9E00]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#1E3A5C]">Logo Settings</h1>
              <p className="text-[#4A5E75] text-sm">Upload and manage the MedStudyPro logo</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Primary Logo (for dark backgrounds) */}
        <div className="bg-white rounded-2xl border border-[#E8EEF3] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1E3A5C] mb-2">Primary Logo</h2>
          <p className="text-sm text-[#4A5E75] mb-4">
            Used on dark backgrounds (landing page hero, footer). Recommended: White or light-colored logo.
          </p>

          <div className="flex flex-col lg:flex-row items-start gap-6">
            {/* Preview on Dark Background */}
            <div className="w-full lg:w-64">
              <p className="text-xs text-slate-500 mb-2">Preview on dark background:</p>
              <div className="h-32 bg-[#192A3F] rounded-xl flex items-center justify-center p-4">
                {logoSettings.logo_url ? (
                  <img 
                    src={`${getFullLogoUrl(logoSettings.logo_url)}?v=${cacheKey}`}
                    alt="Primary Logo" 
                    className="max-w-full max-h-full object-contain"
                    data-testid="primary-logo-preview"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <div className={`text-center text-white/40 ${logoSettings.logo_url ? 'hidden' : ''}`}>
                  <Image className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-xs">No logo uploaded</p>
                </div>
              </div>
            </div>

            {/* Upload Controls */}
            <div className="flex-1">
              <input
                type="file"
                ref={primaryFileInputRef}
                className="hidden"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => handleUpload(e.target.files?.[0], false)}
              />
              
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => primaryFileInputRef.current?.click()}
                  disabled={uploading}
                  className="bg-[#FF9E00] hover:bg-[#E88D00] text-white"
                  data-testid="upload-primary-logo-btn"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </>
                  )}
                </Button>

                {logoSettings.logo_url && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => openPreviewModal(logoSettings.logo_url, 'Primary Logo - Full Size')}
                      className="text-[#1E3A5C] border-[#D4E3EF]"
                      data-testid="view-primary-logo-btn"
                    >
                      <ZoomIn className="w-4 h-4 mr-2" />
                      View Full Size
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(false)}
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      data-testid="delete-primary-logo-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </>
                )}
              </div>

              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 font-medium mb-2">Guidelines:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Formats: PNG, JPEG, SVG, WebP</li>
                  <li>• Max size: 5MB</li>
                  <li>• Use white/light colors for dark backgrounds</li>
                  <li>• Square or horizontal aspect ratio works best</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Logo (for light backgrounds) */}
        <div className="bg-white rounded-2xl border border-[#E8EEF3] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1E3A5C] mb-2">Secondary Logo</h2>
          <p className="text-sm text-[#4A5E75] mb-4">
            Used on light backgrounds (dashboard, admin pages). Recommended: Dark or colored logo.
          </p>

          <div className="flex flex-col lg:flex-row items-start gap-6">
            {/* Preview on Light Background */}
            <div className="w-full lg:w-64">
              <p className="text-xs text-slate-500 mb-2">Preview on light background:</p>
              <div className="h-32 bg-[#F8FAFD] border border-[#E8EEF3] rounded-xl flex items-center justify-center p-4">
                {logoSettings.logo_light_url ? (
                  <img 
                    src={`${getFullLogoUrl(logoSettings.logo_light_url)}?v=${cacheKey}`}
                    alt="Secondary Logo" 
                    className="max-w-full max-h-full object-contain"
                    data-testid="secondary-logo-preview"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <div className={`text-center text-slate-400 ${logoSettings.logo_light_url ? 'hidden' : ''}`}>
                  <Image className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-xs">No logo uploaded</p>
                </div>
              </div>
            </div>

            {/* Upload Controls */}
            <div className="flex-1">
              <input
                type="file"
                ref={secondaryFileInputRef}
                className="hidden"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={(e) => handleUpload(e.target.files?.[0], true)}
              />
              
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={() => secondaryFileInputRef.current?.click()}
                  disabled={uploadingLight}
                  className="bg-[#FF9E00] hover:bg-[#E88D00] text-white"
                  data-testid="upload-secondary-logo-btn"
                >
                  {uploadingLight ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Logo
                    </>
                  )}
                </Button>

                {logoSettings.logo_light_url && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => openPreviewModal(logoSettings.logo_light_url, 'Secondary Logo - Full Size')}
                      className="text-[#1E3A5C] border-[#D4E3EF]"
                      data-testid="view-secondary-logo-btn"
                    >
                      <ZoomIn className="w-4 h-4 mr-2" />
                      View Full Size
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(true)}
                      className="text-red-500 border-red-200 hover:bg-red-50"
                      data-testid="delete-secondary-logo-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  </>
                )}
              </div>

              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 font-medium mb-2">Guidelines:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>• Formats: PNG, JPEG, SVG, WebP</li>
                  <li>• Max size: 5MB</li>
                  <li>• Use dark/colored version for light backgrounds</li>
                  <li>• Square or horizontal aspect ratio works best</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Where Logos Appear */}
        <div className="bg-white rounded-2xl border border-[#E8EEF3] p-6">
          <h2 className="text-lg font-semibold text-[#1E3A5C] mb-4">Where Logos Appear</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 bg-[#192A3F] rounded-xl">
              <p className="text-xs text-white/60 mb-2">Primary Logo (Dark BG):</p>
              <ul className="text-xs text-white/80 space-y-1">
                <li>• Landing page header</li>
                <li>• Landing page footer</li>
                <li>• Topics page navigation</li>
                <li>• About/Contact page headers</li>
              </ul>
            </div>
            <div className="p-4 bg-[#F8FAFD] border border-[#E8EEF3] rounded-xl">
              <p className="text-xs text-slate-500 mb-2">Secondary Logo (Light BG):</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Dashboard navigation</li>
                <li>• Admin panel header</li>
                <li>• All logged-in pages</li>
              </ul>
            </div>
          </div>
        </div>

        {logoSettings.updated_at && (
          <p className="text-xs text-slate-400 text-center mt-6">
            Last updated: {new Date(logoSettings.updated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
